/* =========================================================================
   ClueVine — Bloom (fill the vowels)
   The consonant skeleton is shown; the vowel positions are empty. Fill them in
   to make the word bloom. Any valid word that fits the skeleton counts, so it's
   forgiving — and the answer is guaranteed to be one of them.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  GenerateContext,
  DifficultyLevel,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export const VOWELS = 'aeiou';

export interface BloomConfig {
  length: number;
  maxAttempts: number;
}

export interface BloomState {
  answer: string;
  pattern: string[]; // consonants in place, '' at vowel slots
  vowelSlots: number[]; // positions to fill, in order
  solutions: string[]; // every valid word matching the skeleton
  filled: Record<number, string>; // position -> vowel
  length: number;
  maxAttempts: number;
  attempts: number;
  solved: boolean;
  revealed: number[];
}

export type BloomMove =
  | { type: 'fill'; ch: string }
  | { type: 'remove' }
  | { type: 'submit' };

export const BLOOM_DIFFICULTIES: DifficultyLevel<BloomConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '5 letters', scoreMultiplier: 0.9, config: { length: 5, maxAttempts: 6 } },
  { id: 'standard', label: 'Garden', blurb: '6 letters', scoreMultiplier: 1, config: { length: 6, maxAttempts: 5 } },
  { id: 'hard', label: 'Thicket', blurb: '7 letters', scoreMultiplier: 1.35, config: { length: 7, maxAttempts: 5 } },
];

export function vowelCount(word: string): number {
  let n = 0;
  for (const ch of word) if (VOWELS.includes(ch)) n += 1;
  return n;
}

export function matchesPattern(word: string, pattern: string[]): boolean {
  if (word.length !== pattern.length) return false;
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '') {
      if (!VOWELS.includes(word[i])) return false;
    } else if (word[i] !== pattern[i]) {
      return false;
    }
  }
  return true;
}

export function assembled(state: BloomState): string {
  return state.pattern.map((c, i) => (c !== '' ? c : state.filled[i] ?? '')).join('');
}

export function allFilled(state: BloomState): boolean {
  return state.vowelSlots.every((s) => Boolean(state.filled[s]));
}

export function generate(ctx: GenerateContext<BloomConfig>): BloomState {
  const { length, maxAttempts } = ctx.difficulty.config;
  const full = ctx.dictionary.answersByLength(length);
  const pool = full.filter((w) => {
    const v = vowelCount(w);
    return v >= 2 && v <= length - 2;
  });
  const answer = pickAvoiding(ctx.prng, pool.length > 0 ? pool : full, ctx.avoid);

  const pattern = answer.split('').map((c) => (VOWELS.includes(c) ? '' : c));
  const vowelSlots: number[] = [];
  pattern.forEach((c, i) => {
    if (c === '') vowelSlots.push(i);
  });
  const solutions = ctx.dictionary.byLength(length).filter((w) => matchesPattern(w, pattern));

  return {
    answer,
    pattern,
    vowelSlots,
    solutions: solutions.length > 0 ? solutions : [answer],
    filled: {},
    length,
    maxAttempts,
    attempts: 0,
    solved: false,
    revealed: [],
  };
}

export function status(state: BloomState): PuzzleStatus {
  if (state.solved) return 'won';
  if (state.attempts >= state.maxAttempts) return 'lost';
  return 'playing';
}

export function validateMove(state: BloomState, move: BloomMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  if (!allFilled(state)) return { ok: false, reason: 'Fill in every vowel' };
  return { ok: true };
}

function nextEmptySlot(state: BloomState): number | null {
  for (const s of state.vowelSlots) if (!state.filled[s]) return s;
  return null;
}

function lastFilledSlot(state: BloomState): number | null {
  for (let i = state.vowelSlots.length - 1; i >= 0; i--) {
    const s = state.vowelSlots[i];
    // Vowels revealed by a hint are locked in.
    if (state.filled[s] && !state.revealed.includes(s)) return s;
  }
  return null;
}

export function reduce(state: BloomState, move: BloomMove): BloomState {
  switch (move.type) {
    case 'fill': {
      if (!VOWELS.includes(move.ch)) return state;
      const slot = nextEmptySlot(state);
      if (slot == null) return state;
      return { ...state, filled: { ...state.filled, [slot]: move.ch } };
    }
    case 'remove': {
      const slot = lastFilledSlot(state);
      if (slot == null) return state;
      const filled = { ...state.filled };
      delete filled[slot];
      return { ...state, filled };
    }
    case 'submit': {
      if (!allFilled(state)) return state;
      const word = assembled(state);
      if (state.solutions.includes(word)) return { ...state, solved: true };
      return { ...state, attempts: state.attempts + 1 };
    }
    default:
      return state;
  }
}

export function handleKey(state: BloomState, key: InputKey): BloomMove | null {
  if (key.type === 'char') {
    return VOWELS.includes(key.value) && nextEmptySlot(state) != null
      ? { type: 'fill', ch: key.value }
      : null;
  }
  if (key.type === 'backspace') {
    return lastFilledSlot(state) != null ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function hint(state: BloomState): HintOutcome<BloomState> | null {
  if (status(state) !== 'playing') return null;
  // Reveal a vowel the player doesn't already have right.
  const slot = state.vowelSlots.find((s) => !state.revealed.includes(s) && state.filled[s] !== state.answer[s]);
  if (slot == null) return null;
  return {
    message: `Vowel ${state.vowelSlots.indexOf(slot) + 1} is “${state.answer[slot].toUpperCase()}”.`,
    penalty: 45,
    state: {
      ...state,
      revealed: [...state.revealed, slot],
      filled: { ...state.filled, [slot]: state.answer[slot] },
    },
  };
}

export function moveFeedback(prev: BloomState, next: BloomState): MoveFeedback | null {
  if (next.attempts > prev.attempts && !next.solved) {
    const left = next.maxAttempts - next.attempts;
    return left > 0
      ? { message: `Not a word — ${left} ${left === 1 ? 'try' : 'tries'} left`, tone: 'error' }
      : { message: 'Out of tries', tone: 'error' };
  }
  return null;
}

export function score(state: BloomState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs: state.length * 7000 };
  }
  const base = 160 + (state.maxAttempts - state.attempts) * 150 + state.length * 40;
  return {
    base,
    lines: [{ label: `Bloomed (${state.attempts} miss${state.attempts === 1 ? '' : 'es'})`, value: base, kind: 'base' }],
    parMs: state.length * 7000,
    perfect: state.attempts === 0 && state.revealed.length === 0,
  };
}

export function summarize(state: BloomState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Bloomed in ${state.attempts + 1}` : 'Wilted',
    answer: assembled(state) && won ? assembled(state).toUpperCase() : state.answer.toUpperCase(),
    attempts: state.attempts,
    avoidKeys: [state.answer],
    shareGrid: [won ? `🌸 solved in ${state.attempts + 1}` : '🥀 wilted'],
  };
}

export function deserialize(raw: unknown): BloomState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<BloomState>;
  if (typeof s.answer !== 'string' || !Array.isArray(s.pattern) || !Array.isArray(s.vowelSlots)) {
    return null;
  }
  if (!Array.isArray(s.solutions) || typeof s.length !== 'number') return null;
  return {
    answer: s.answer,
    pattern: s.pattern.filter((c): c is string => typeof c === 'string'),
    vowelSlots: s.vowelSlots.filter((n): n is number => typeof n === 'number'),
    solutions: s.solutions.filter((w): w is string => typeof w === 'string'),
    filled: (s.filled && typeof s.filled === 'object' ? s.filled : {}) as Record<number, string>,
    length: s.length,
    maxAttempts: typeof s.maxAttempts === 'number' ? s.maxAttempts : 5,
    attempts: typeof s.attempts === 'number' ? s.attempts : 0,
    solved: Boolean(s.solved),
    revealed: Array.isArray(s.revealed) ? s.revealed.filter((n): n is number => typeof n === 'number') : [],
  };
}
